import dayjs from 'dayjs';

import {
  Task,
  TaskGroupBy,
  TaskMemberRole,
  TaskPriority,
  TaskSortOption,
  User,
} from '@graphql/generated';
import theme from '@themes/theme';
import { beginningOfToday, localDateFromUTCDate } from '@utils/formatters/date';

/**
 * Pure grouping/sorting logic for the My Tasks mobile revamp (TA-10186).
 * Kept free of React/Apollo so each rule is unit-testable in isolation.
 */

export enum MyTasksTab {
  MyDay = 'MY_DAY',
  SevenDays = 'SEVEN_DAYS',
  AllTasks = 'ALL_TASKS',
}

export enum MyTasksGroupBy {
  Project = 'PROJECT',
  Priority = 'PRIORITY',
  Assignee = 'ASSIGNEE',
}

export const UNASSIGNED_GROUP_KEY = 'assignee:none';

/** Maps the filter sheet's client-side MyTasksGroupBy to the GraphQL
 * TaskGroupBy enum listMyTasksProjectGroups expects (TA-10373) — the two are
 * string-identical today, but this indirection keeps the query call sites
 * decoupled from that coincidence. */
export const TASK_GROUP_BY_FOR: Record<MyTasksGroupBy, TaskGroupBy> = {
  [MyTasksGroupBy.Project]: TaskGroupBy.Project,
  [MyTasksGroupBy.Priority]: TaskGroupBy.Priority,
  [MyTasksGroupBy.Assignee]: TaskGroupBy.Assignee,
};

/**
 * In-group ordering options offered by the filter sheet (TA-10187 Figma
 * "Order By" row). OverdueToLow is the TA-10186 AC5 default.
 */
export enum MyTasksOrderBy {
  OverdueToLow = 'OVERDUE_TO_LOW',
  LastUpdated = 'LAST_UPDATED',
  CreatedDate = 'CREATED_DATE',
}

/** Maps the filter sheet's Order By option to the GraphQL TaskSortOption the
 * server understands, so changing Order By issues a real server-side sort
 * (and refetch) instead of re-sorting client-side. The server now owns
 * ordering entirely — OVERDUE_LOW implements the overdue-pinned-on-top +
 * priority-desc + id-asc rule server-side, so the client no longer needs
 * (or runs) its own version of that logic. */
export const TASK_SORT_BY_FOR: Record<MyTasksOrderBy, TaskSortOption> = {
  [MyTasksOrderBy.OverdueToLow]: TaskSortOption.OverdueLow,
  [MyTasksOrderBy.LastUpdated]: TaskSortOption.UpdatedAtDesc,
  [MyTasksOrderBy.CreatedDate]: TaskSortOption.CreatedAtDesc,
};

export type MyTaskGroup = {
  key: string;
  title: string;
  /** Open (not completed) tasks, sorted Overdue → High → Medium → Low. */
  tasks: Task[];
  /** Completed tasks scoped to this group, collapsed by default (AC10). */
  completedTasks: Task[];
  /**
   * Recency proxy used to order group cards (AC9). Task has no updatedAt in
   * the schema, so we use the latest completedAt/createdAt among the group's
   * tasks as "recently active".
   */
  lastActivityAt: number;
  /** Set only for Assignee grouping (AC8 follow-up) — the group's assignee. */
  assigneeUser?: User;
  /**
   * True open/completed counts for this group straight from the server's
   * TaskGroupInterface (`ProjectTaskGroup`/`PriorityTaskGroup`/
   * `AssigneeTaskGroup` all expose taskCount/completedCount) — undefined for
   * the client-grouped path (`groupTasks`, ProjectTasksScreen), which has no
   * server-side total and falls back to counting `tasks`/`completedTasks`
   * directly. Server-grouped counts must come from here, not from
   * `tasks.length`/`completedTasks.length` — each group's own tasks/
   * completedTasks connection is capped at first:100, so counting the loaded
   * array undercounts once a project has more than 100 combined tasks.
   */
  taskCount?: number;
  completedCount?: number;
  /**
   * Whether this group has more open/completed tasks beyond the loaded
   * first:100 page (TA-10446) — undefined for the client-grouped path
   * (`groupTasks`, ProjectTasksScreen), which has no per-group pagination.
   * MyTasksScreen uses these to trigger `fetchMoreGroupTasks` per card; only
   * wired for the 7 Days / All Tasks tabs (single query per tab) — My Day
   * merges two due_group-scoped queries per group, so "load more" would be
   * ambiguous about which due_group scope to continue and isn't supported.
   */
  tasksHasMore?: boolean;
  completedTasksHasMore?: boolean;
  /** Cursor to continue this group's own tasks/completedTasks connection
   * from (TA-10446) — paired with tasksHasMore/completedTasksHasMore above. */
  tasksEndCursor?: string | null;
  completedTasksEndCursor?: string | null;
  /**
   * Raw dimension identity (TA-10446) — lets a caller scope a
   * fetchMoreGroupTasks call to this specific group without re-parsing
   * `key`. Mutually exclusive per groupBy, mirrors ProjectGroupInput's
   * project/priority/assignee (Assignee's identity is `assigneeUser?.id`
   * above, so isn't repeated here).
   */
  projectId?: string;
  priorityValue?: TaskPriority;
};

/** Max number of cards in the overdue hero carousel (AC12). */
export const HERO_MAX_CARDS = 5;

/**
 * Hero background rotation (AC12): slides 1 & 4 share a color, 2 & 5 share a
 * color, 3 has its own. Tokens confirmed against the Figma fills (TA-10186).
 */
export const HERO_BACKGROUND_COLORS: (keyof typeof theme.colors)[] = [
  'blueMild',
  'purpleMild',
  'magentaLightMild',
];

export const heroColorForIndex = (index: number): keyof typeof theme.colors =>
  HERO_BACKGROUND_COLORS[index % HERO_BACKGROUND_COLORS.length];

const localDueDate = (task: Task): Date | undefined =>
  task.dueDate ? localDateFromUTCDate(task.dueDate) : undefined;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Overdue = due strictly before today (due today is NOT overdue). */
export const isTaskOverdue = (
  task: Task,
  today = beginningOfToday()
): boolean => {
  const due = localDueDate(task);
  if (!due || task.completedAt) return false;
  return startOfDay(due) < startOfDay(today);
};

export const isTaskDueToday = (
  task: Task,
  today = beginningOfToday()
): boolean => {
  const due = localDueDate(task);
  if (!due) return false;
  return startOfDay(due).getTime() === startOfDay(today).getTime();
};

/**
 * Due today or earlier, ignoring completedAt (unlike isTaskOverdue).
 * Used to scope completed tasks on My Day (AC2/AC10) — a task completed
 * yesterday should still count as "due today or earlier".
 */
export const isTaskDueTodayOrEarlier = (
  task: Task,
  today = beginningOfToday()
): boolean => {
  const due = localDueDate(task);
  if (!due) return false;
  return startOfDay(due) <= startOfDay(today);
};

/** Due tomorrow through the next 7 days (AC3). */
export const isTaskDueWithinNext7Days = (
  task: Task,
  today = beginningOfToday()
): boolean => {
  const due = localDueDate(task);
  if (!due) return false;
  const start = startOfDay(today);
  const tomorrow = new Date(start);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  const dueDay = startOfDay(due);
  return dueDay >= tomorrow && dueDay <= end;
};

const PRIORITY_RANK: Record<TaskPriority, number> = {
  [TaskPriority.High]: 0,
  [TaskPriority.Medium]: 1,
  [TaskPriority.Low]: 2,
};

const priorityRank = (task: Task): number =>
  task.priority
    ? PRIORITY_RANK[task.priority]
    : PRIORITY_RANK[TaskPriority.Low];

const dueTime = (task: Task): number => {
  const due = localDueDate(task);
  // Undated tasks sort after any dated task within the same priority bucket.
  return due ? due.getTime() : Number.MAX_SAFE_INTEGER;
};

const createdTime = (task: Task): number =>
  task.createdAt ? new Date(task.createdAt).getTime() : 0;

/**
 * Default in-group order (AC5/AC6): overdue pinned on top (soonest due date
 * first), then High → Medium → Low; ties broken by due date, undated last.
 *
 * Alternative orderings come from the filter sheet's Order By row
 * (TA-10187). Overdue items stay pinned on top in every mode (AC6).
 */
/**
 * Final tie-break, mirroring the server's trailing `tasks.id ASC`. Without it
 * equal keys (same due date, same priority) kept whatever order the input had
 * — and that depends on which connection a task arrived on, which changes
 * when the task is toggled — so a reopened task first sat behind its
 * same-date siblings and then jumped ahead of them when the refetch landed
 * (TA-11016).
 */
const compareIds = (a: Task, b: Task): number => {
  const aNum = Number(a.id);
  const bNum = Number(b.id);
  if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) {
    return aNum - bNum;
  }
  return a.id.localeCompare(b.id);
};

const compareOpenTasks = (
  a: Task,
  b: Task,
  today: Date,
  orderBy: MyTasksOrderBy
): number => {
  const aOverdue = isTaskOverdue(a, today);
  const bOverdue = isTaskOverdue(b, today);
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  if (aOverdue && bOverdue) return dueTime(a) - dueTime(b);
  switch (orderBy) {
    case MyTasksOrderBy.LastUpdated:
      // Task exposes no updatedAt; latest completedAt/createdAt is the
      // closest available "last activity" signal (see MyTaskGroup note).
      return taskActivityAt(b) - taskActivityAt(a);
    case MyTasksOrderBy.CreatedDate:
      return createdTime(b) - createdTime(a);
    default: {
      const rankDiff = priorityRank(a) - priorityRank(b);
      if (rankDiff !== 0) return rankDiff;
      return dueTime(a) - dueTime(b);
    }
  }
};

export const sortTasksInGroup = (
  tasks: Task[],
  today = beginningOfToday(),
  orderBy: MyTasksOrderBy = MyTasksOrderBy.OverdueToLow
): Task[] =>
  [...tasks].sort(
    (a, b) => compareOpenTasks(a, b, today, orderBy) || compareIds(a, b)
  );

/**
 * A group's completed rows in the server's OVERDUE_LOW order
 * (`due_date ASC NULLS LAST, priority DESC, id ASC`). They used to be shown
 * in whatever order the connections delivered them, so a just-completed task
 * went to the top of the completed rows and then moved when the refetch
 * returned the server's order (TA-11016).
 */
export const sortCompletedTasksInGroup = (tasks: Task[]): Task[] =>
  [...tasks].sort(
    (a, b) =>
      dueTime(a) - dueTime(b) ||
      priorityRank(a) - priorityRank(b) ||
      compareIds(a, b)
  );

/** Hero cards: overdue only, soonest overdue date first, capped at 5 (AC12/13). */
export const selectHeroTasks = (overdueTasks: Task[]): Task[] =>
  [...overdueTasks]
    .filter((t) => !t.completedAt)
    .sort((a, b) => dueTime(a) - dueTime(b))
    .slice(0, HERO_MAX_CARDS);

export const taskActivityAt = (task: Task): number => {
  const completed = task.completedAt ? new Date(task.completedAt).getTime() : 0;
  const created = task.createdAt ? new Date(task.createdAt).getTime() : 0;
  return Math.max(completed, created);
};

const primaryAssignee = (task: Task) =>
  task.members?.find((m) => m.role === TaskMemberRole.Assignee)?.user;

type GroupDescriptor = { key: string; title: string; assigneeUser?: User };

const groupDescriptor = (
  task: Task,
  groupBy: MyTasksGroupBy
): GroupDescriptor => {
  switch (groupBy) {
    case MyTasksGroupBy.Project:
      return {
        key: `project:${task.projectId}`,
        title: task.project?.name ?? 'Unknown Project',
      };
    case MyTasksGroupBy.Priority: {
      const priority = task.priority ?? TaskPriority.Low;
      return { key: `priority:${priority}`, title: priorityLabel(priority) };
    }
    case MyTasksGroupBy.Assignee: {
      const assignee = primaryAssignee(task);
      return assignee
        ? {
            key: `assignee:${assignee.id}`,
            title: assignee.name ?? 'Unknown',
            assigneeUser: assignee,
          }
        : { key: UNASSIGNED_GROUP_KEY, title: 'Unassigned' };
    }
  }
};

export const priorityLabel = (priority: TaskPriority): string => {
  switch (priority) {
    case TaskPriority.High:
      return 'High Priority';
    case TaskPriority.Medium:
      return 'Medium Priority';
    default:
      return 'Low Priority';
  }
};

const PRIORITY_GROUP_ORDER = [
  `priority:${TaskPriority.High}`,
  `priority:${TaskPriority.Medium}`,
  `priority:${TaskPriority.Low}`,
];

/**
 * Re-splits two independently-fetched task lists by each task's *current*
 * `completedAt`, de-duplicating on id.
 *
 * Search has no server-grouped query: open and completed results come from two
 * separate `search` requests, and which list a task arrived in only describes
 * its state when that request ran. A completion toggled afterwards updates the
 * normalized Task (optimistic response, then the mutation result) but not which
 * list holds it, and the primary search is cache-first — so re-running the same
 * term serves the same stale open list. Bucketing by the list of origin left a
 * completed task in the open list indefinitely, even after a fresh search
 * (TA-11018).
 *
 * The lists themselves can also hold stale snapshots (Project Task Search is
 * `no-cache`; Global Search appends load-more pages without refreshing earlier
 * ones), so the list objects are not the source of truth either. Where
 * `liveCompletedAt` (the normalized cache, see useLiveTaskCompletedAt) has a
 * value for a task, it wins, and the task is returned patched with it so its
 * row's tick agrees with the section it lands in. A task in both lists is kept
 * once, open-list order first — which copy is kept no longer matters, because
 * its bucket comes from the live value, not from that copy.
 */
export const partitionTasksByCompletion = (
  openTasks: Task[],
  completedTasks: Task[],
  liveCompletedAt: ReadonlyMap<string, string | null> = new Map()
): { openTasks: Task[]; completedTasks: Task[] } => {
  const seen = new Set<string>();
  const open: Task[] = [];
  const completed: Task[] = [];
  [...openTasks, ...completedTasks].forEach((task) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    const completedAt = liveCompletedAt.has(task.id)
      ? liveCompletedAt.get(task.id)
      : task.completedAt;
    const current =
      completedAt === task.completedAt ? task : { ...task, completedAt };
    (completedAt ? completed : open).push(current);
  });
  return { openTasks: open, completedTasks: completed };
};

/**
 * Groups open + completed tasks under the selected dimension. Completed tasks
 * only ever appear inside the collapsed section of a group (AC10); groups
 * whose open list is empty but have completed tasks still render so the user
 * can expand them.
 *
 * Group cards are ordered by recent activity (AC9) except for Priority
 * grouping, which uses the fixed High → Medium → Low order.
 */
export const groupTasks = (
  openTasks: Task[],
  completedTasks: Task[],
  groupBy: MyTasksGroupBy,
  today = beginningOfToday(),
  orderBy: MyTasksOrderBy = MyTasksOrderBy.OverdueToLow
): MyTaskGroup[] => {
  const groups = new Map<
    string,
    { title: string; open: Task[]; completed: Task[]; assigneeUser?: User }
  >();

  const bucketFor = (task: Task) => {
    const { key, title, assigneeUser } = groupDescriptor(task, groupBy);
    let bucket = groups.get(key);
    if (!bucket) {
      bucket = { title, open: [], completed: [], assigneeUser };
      groups.set(key, bucket);
    }
    return bucket;
  };

  openTasks.forEach((task) => bucketFor(task).open.push(task));
  completedTasks.forEach((task) => bucketFor(task).completed.push(task));

  const result: MyTaskGroup[] = [...groups.entries()].map(([key, bucket]) => ({
    key,
    title: bucket.title,
    tasks: sortTasksInGroup(bucket.open, today, orderBy),
    completedTasks: bucket.completed,
    lastActivityAt: Math.max(
      0,
      ...bucket.open.map(taskActivityAt),
      ...bucket.completed.map(taskActivityAt)
    ),
    assigneeUser: bucket.assigneeUser,
  }));

  if (groupBy === MyTasksGroupBy.Priority) {
    return result.sort(
      (a, b) =>
        PRIORITY_GROUP_ORDER.indexOf(a.key) -
        PRIORITY_GROUP_ORDER.indexOf(b.key)
    );
  }

  return result.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
};

/** Header labels above the group list, e.g. "PROJECT GROUP" / "2 projects". */
export const groupSectionLabel = (groupBy: MyTasksGroupBy): string => {
  switch (groupBy) {
    case MyTasksGroupBy.Project:
      return 'PROJECT GROUP';
    case MyTasksGroupBy.Priority:
      return 'PRIORITY GROUP';
    case MyTasksGroupBy.Assignee:
      return 'ASSIGNEE GROUP';
  }
};

const GROUP_COUNT_NOUNS: Record<MyTasksGroupBy, [string, string]> = {
  [MyTasksGroupBy.Project]: ['project', 'projects'],
  [MyTasksGroupBy.Priority]: ['priority', 'priorities'],
  [MyTasksGroupBy.Assignee]: ['assignee', 'assignees'],
};

/**
 * `hasMore` (server-paginated groups, e.g. Project grouping backed by
 * listMyTasksProjectGroups) appends "+" to the loaded count, e.g. "50+
 * projects" — a next page exists beyond what's currently loaded.
 */
export const groupCountLabel = (
  groupBy: MyTasksGroupBy,
  count: number,
  hasMore = false
): string => {
  const [singular, plural] = GROUP_COUNT_NOUNS[groupBy];
  return `${count}${hasMore ? '+' : ''} ${count === 1 ? singular : plural}`;
};

export type MyDayTabInput = {
  overdueTasks: Task[];
  todayTasks: Task[];
};

/**
 * My Day (AC2): everything due today plus overdue items. Both lists come from
 * separate server queries; de-dupe defensively on id.
 */
export const composeMyDayTasks = ({
  overdueTasks,
  todayTasks,
}: MyDayTabInput): Task[] => {
  const seen = new Set<string>();
  const result: Task[] = [];
  [...overdueTasks, ...todayTasks].forEach((task) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    result.push(task);
  });
  return result;
};

/**
 * All Tasks (AC4): overdue + current (dated), de-duplicated by id. Unscheduled
 * tasks are no longer split out here — they're fetched directly via a
 * dedicated `listTasks(withoutDueDate: true)` query so the server does the
 * filtering instead of the client.
 */
export const composeAllTasks = (
  overdueTasks: Task[],
  currentTasks: Task[]
): Task[] => {
  const seen = new Set<string>();
  const scheduled: Task[] = [];
  [...overdueTasks, ...currentTasks].forEach((task) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    scheduled.push(task);
  });
  return scheduled;
};

/** Minimal local mirror of a Relay PageInfo — kept local (rather than
 * importing the generated GraphQL type) so this file stays free of
 * React/Apollo dependencies. */
export type GroupConnectionPageInfo = {
  hasNextPage: boolean;
  endCursor?: string | null;
};

/** Structural shape of one entry from the `listMyTasksProjectGroups` query —
 * kept minimal/local (rather than importing the Apollo hook's types) so this
 * file stays free of React/Apollo dependencies. `project`/`priority`/`assignee`
 * are mutually exclusive per the server's TaskGroupInterface union
 * (ProjectTaskGroup | PriorityTaskGroup | AssigneeTaskGroup, TA-10373); which
 * one is populated depends on the `groupBy` the query was sent with. */
export type ProjectGroupInput = {
  project?: { id: string; name?: string | null } | null;
  priority?: TaskPriority | null;
  assignee?: User | null;
  taskCount?: number;
  completedCount?: number;
  tasks: Task[];
  completedTasks: Task[];
  /** Pagination state for this group's own tasks/completedTasks connections
   * (TA-10446) — undefined until the hook starts surfacing it. */
  tasksPageInfo?: GroupConnectionPageInfo;
  completedTasksPageInfo?: GroupConnectionPageInfo;
};

/** "Is this task open (not completed)?" — by the Task's own, possibly
 * optimistic, completedAt. Shared by every consumer that buckets a task list
 * into open/completed (`composeServerGroups`, `composeUnscheduledGroup`). */
const isTaskOpen = (task: Task): boolean => !task.completedAt;

/**
 * completedAt the screen has decided on for a just-toggled task, keyed by id.
 * TasksListView holds one per toggle until a refetch that started *after*
 * the toggle settled has landed (TA-11016).
 *
 * Needed because Apollo's optimistic layer only shields a task while its own
 * mutation is in flight. Once it settles, any response that was requested
 * earlier — the coalesced refetch for an earlier batch of taps, or a
 * background poll — still carries the pre-toggle state and overwrites the
 * cache when it lands, so a rapidly tapped task visibly went completed →
 * reopened → completed again until the next refetch caught up.
 */
export type CompletedAtOverrides = ReadonlyMap<string, string | null>;

const withCompletionOverride =
  (overrides?: CompletedAtOverrides) =>
  (task: Task): Task => {
    if (!overrides?.has(task.id)) return task;
    const completedAt = overrides.get(task.id) ?? null;
    return (task.completedAt ?? null) === completedAt
      ? task
      : { ...task, completedAt };
  };

/** Same key/title/assigneeUser derivation as `groupDescriptor` above, but
 * sourced from a server group (`ProjectGroupInput`) instead of a `Task` —
 * used by `composeServerGroups`/`mergeServerGroups` once the corresponding
 * dimension is server-grouped (TA-10373). */
const serverGroupDescriptor = (
  group: ProjectGroupInput,
  groupBy: MyTasksGroupBy
): GroupDescriptor => {
  switch (groupBy) {
    case MyTasksGroupBy.Project:
      return {
        key: `project:${group.project?.id ?? 'unknown'}`,
        title: group.project?.name ?? 'Unknown Project',
      };
    case MyTasksGroupBy.Priority: {
      const priority = group.priority ?? TaskPriority.Low;
      return { key: `priority:${priority}`, title: priorityLabel(priority) };
    }
    case MyTasksGroupBy.Assignee: {
      const assignee = group.assignee;
      return assignee
        ? {
            key: `assignee:${assignee.id}`,
            title: assignee.name ?? 'Unknown',
            assigneeUser: assignee,
          }
        : { key: UNASSIGNED_GROUP_KEY, title: 'Unassigned' };
    }
  }
};

/**
 * Converts server-side task groups (from `listMyTasksProjectGroups`, any of
 * the three `groupBy` dimensions, TA-10373) into the same `MyTaskGroup[]`
 * shape the client-side `groupTasks` produces, so `MyTaskGroupCard` doesn't
 * need to know which path the data came from.
 *
 * Task order within each group is whatever the server returned — the query's
 * `sortBy` (see TASK_SORT_BY_FOR) already implements Order By server-side,
 * including OVERDUE_LOW's overdue-pinned-on-top rule, so this no longer
 * re-sorts client-side (that used to fight the server's own ordering).
 *
 * Group *card* ordering mirrors each dimension's server-side ORDER BY so a
 * merge across multiple due_group-scoped queries (My Day) still lands in the
 * right order: Project by recent activity desc, Priority fixed High → Medium
 * → Low, Assignee alphabetical with Unassigned pinned last.
 */
export const composeServerGroups = (
  serverGroups: ProjectGroupInput[],
  groupBy: MyTasksGroupBy,
  /** See CompletedAtOverrides. */
  completedAtOverrides?: CompletedAtOverrides
): MyTaskGroup[] => {
  const applyOverride = withCompletionOverride(completedAtOverrides);
  const result: MyTaskGroup[] = serverGroups.map((group) => {
    const { key, title, assigneeUser } = serverGroupDescriptor(group, groupBy);
    // Re-bucket by the Task entity's own completedAt rather than trusting
    // which connection each task arrived on. A completeTask optimisticResponse
    // patches completedAt on the normalized Task in-place (TA-10446 bug), so
    // the row moves to its new section the moment it is tapped — per the
    // prototype, with no in-flight spinner (TA-11016) — instead of waiting
    // for the refetch round trip.
    // An override (see CompletedAtOverrides) wins over a stale response.
    // Deduped by id: the same task can sit in both connections at once — My
    // Day merges the Overdue and Current queries (mergeServerGroups only
    // dedupes open-vs-open and completed-vs-completed), and right after a
    // toggle one query's refetch can land with the task in its new section
    // while the other still holds it in the old one. Both entries point at
    // the same normalized Task, so without this the row renders twice.
    const seenIds = new Set<string>();
    const allTasks = [...group.tasks, ...group.completedTasks]
      .filter((task) => {
        if (seenIds.has(task.id)) return false;
        seenIds.add(task.id);
        return true;
      })
      .map(applyOverride);
    const tasks = allTasks.filter(isTaskOpen);
    const completedTasks = allTasks.filter((task) => !isTaskOpen(task));

    // taskCount/completedCount are the server's real totals (see MyTaskGroup
    // doc comment — each connection is capped at first:100, so they can't be
    // derived from tasks.length/completedTasks.length for large groups). The
    // re-bucketing above already moves a just-toggled task between `tasks`/
    // `completedTasks` instantly, but the server hasn't recomputed these
    // totals yet — without adjusting them here too, the progress bar/count
    // label would keep showing the pre-toggle ratio for the same window the
    // TA-10446 fix eliminated for the row itself, until refetchQueries lands.
    const rawOpenIds = new Set(group.tasks.map((task) => task.id));
    const rawCompletedIds = new Set(
      group.completedTasks.map((task) => task.id)
    );
    // Each total moves by what its own connection got wrong. A task present in
    // both connections is already counted once in each total, so only the
    // stale side is corrected — counting it as a plain toggle would add it to
    // the fresh side a second time (wrong "N/M tasks done").
    const countWhere = (list: Task[], predicate: (id: string) => boolean) =>
      list.filter((task) => predicate(task.id)).length;
    const taskCountDelta =
      countWhere(
        tasks,
        (id) => rawCompletedIds.has(id) && !rawOpenIds.has(id)
      ) - countWhere(completedTasks, (id) => rawOpenIds.has(id));
    const completedCountDelta =
      countWhere(
        completedTasks,
        (id) => rawOpenIds.has(id) && !rawCompletedIds.has(id)
      ) - countWhere(tasks, (id) => rawCompletedIds.has(id));

    return {
      key,
      title,
      tasks,
      completedTasks,
      lastActivityAt: Math.max(
        0,
        ...tasks.map(taskActivityAt),
        ...completedTasks.map(taskActivityAt)
      ),
      assigneeUser,
      taskCount:
        group.taskCount !== undefined
          ? group.taskCount + taskCountDelta
          : undefined,
      completedCount:
        group.completedCount !== undefined
          ? group.completedCount + completedCountDelta
          : undefined,
      tasksHasMore: group.tasksPageInfo?.hasNextPage,
      completedTasksHasMore: group.completedTasksPageInfo?.hasNextPage,
      tasksEndCursor: group.tasksPageInfo?.endCursor,
      completedTasksEndCursor: group.completedTasksPageInfo?.endCursor,
      projectId: group.project?.id,
      priorityValue: group.priority ?? undefined,
    };
  });

  switch (groupBy) {
    case MyTasksGroupBy.Priority:
      return result.sort(
        (a, b) =>
          PRIORITY_GROUP_ORDER.indexOf(a.key) -
          PRIORITY_GROUP_ORDER.indexOf(b.key)
      );
    case MyTasksGroupBy.Assignee:
      return result.sort((a, b) => {
        const aUnassigned = a.key === UNASSIGNED_GROUP_KEY;
        const bUnassigned = b.key === UNASSIGNED_GROUP_KEY;
        if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;
        return a.title.localeCompare(b.title);
      });
    default:
      return result.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
  }
};

/** Fixed key/title of the All Tasks tab's Unscheduled card — a pseudo-group
 * (flat, dimension-less bucket) rather than one of the server's task groups. */
export const UNSCHEDULED_GROUP_KEY = 'unscheduled';

/**
 * Builds the All Tasks tab's Unscheduled pseudo-group (TA-10487) out of the
 * flat `listTasksLight` result the Unscheduled query returns.
 *
 * Unscheduled tasks are the one bucket `listMyTasksProjectGroups` can never
 * carry — it defaults to `requireDueDate: true`, so a task with no due date
 * appears on mobile *only* here. That made this card the single place a
 * completed undated task could ever be shown, and it used to hardcode
 * `completedTasks: []` (backed by an `excludeCompleted: true` query), so
 * completing an undated task removed it from every mobile surface with no way
 * to find it again. Splitting the same open/completed way the server-grouped
 * cards do puts it back behind the card's "Show completed" toggle.
 *
 * Returns null when there's nothing to show at all, which is the card's own
 * render gate.
 */
export const composeUnscheduledGroup = (
  rawTasks: Task[],
  /** See CompletedAtOverrides. */
  completedAtOverrides?: CompletedAtOverrides
): MyTaskGroup | null => {
  if (rawTasks.length === 0) return null;
  const tasks = rawTasks.map(withCompletionOverride(completedAtOverrides));
  return {
    key: UNSCHEDULED_GROUP_KEY,
    title: 'Unscheduled',
    // Order stays whatever the server returned — sortBy already implements it
    // (see TASK_SORT_BY_FOR).
    tasks: tasks.filter(isTaskOpen),
    completedTasks: tasks.filter((task) => !isTaskOpen(task)),
    lastActivityAt: 0,
  };
};

/** The dimension-specific dedupe key `mergeServerGroups` groups by — mirrors
 * `serverGroupDescriptor`'s `key` format so merged buckets line up with the
 * cards `composeServerGroups` later renders. */
const serverGroupMergeKey = (
  group: ProjectGroupInput,
  groupBy: MyTasksGroupBy
): string => {
  switch (groupBy) {
    case MyTasksGroupBy.Project:
      return `project:${group.project?.id ?? 'unknown'}`;
    case MyTasksGroupBy.Priority:
      return `priority:${group.priority ?? 'unknown'}`;
    case MyTasksGroupBy.Assignee:
      return `assignee:${group.assignee?.id ?? 'none'}`;
  }
};

/**
 * Merges multiple server-side task-group results (same `groupBy` dimension)
 * into one, de-duplicating tasks/completedTasks by id within each matching
 * group. My Day has no single due_group covering "overdue + today" (mirrors
 * composeMyDayTasks), so its server-grouped view fetches Overdue and Current
 * separately and merges them here before composeServerGroups builds the
 * final MyTaskGroup[].
 *
 * taskCount/completedCount are each scoped to the due_group the query was
 * sent with (e.g. "3 overdue" + "2 current" for the same project are two
 * separate totals), so merging the same group across sets sums them rather
 * than keeping whichever set's group happened to be seen first.
 */
export const mergeServerGroups = (
  groupBy: MyTasksGroupBy,
  ...serverGroupSets: ProjectGroupInput[][]
): ProjectGroupInput[] => {
  const byKey = new Map<
    string,
    ProjectGroupInput & { tasks: Task[]; completedTasks: Task[] }
  >();
  const seenTaskIds = new Map<string, Set<string>>();
  const seenCompletedTaskIds = new Map<string, Set<string>>();

  serverGroupSets.flat().forEach((group) => {
    const key = serverGroupMergeKey(group, groupBy);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = {
        ...group,
        tasks: [],
        completedTasks: [],
        taskCount: 0,
        completedCount: 0,
      };
      byKey.set(key, bucket);
      seenTaskIds.set(key, new Set());
      seenCompletedTaskIds.set(key, new Set());
    }
    bucket.taskCount = (bucket.taskCount ?? 0) + (group.taskCount ?? 0);
    bucket.completedCount =
      (bucket.completedCount ?? 0) + (group.completedCount ?? 0);
    const taskIds = seenTaskIds.get(key) as Set<string>;
    group.tasks.forEach((task) => {
      if (taskIds.has(task.id)) return;
      taskIds.add(task.id);
      bucket.tasks.push(task);
    });
    const completedIds = seenCompletedTaskIds.get(key) as Set<string>;
    group.completedTasks.forEach((task) => {
      if (completedIds.has(task.id)) return;
      completedIds.add(task.id);
      bucket.completedTasks.push(task);
    });
  });

  return [...byKey.values()];
};

/**
 * Keeps a card this session's own taps touched on screen when the server
 * stops returning it (TA-11016). A group's rank is its most recent task
 * activity, and a completion is activity, so reopening a task can drop its
 * project far down the server order. The toggle's refetch only asks for the
 * first page of groups, so a dropped card vanished, then came back on the
 * next page at a different place — the "card jumps after reopen" QA saw.
 *
 * `retained` (mutated) remembers the latest server copy of every touched
 * group. A touched group missing from `serverGroups` is appended from that
 * copy; the card order pin places it. Its rows take their completedAt from
 * `readLiveCompletedAt` (the Task entity in the cache), not from the stored
 * copy, and a row whose entity can't be read is dropped, so the card never
 * shows a state the server has since replaced.
 */
export const retainTouchedGroups = (
  serverGroups: ProjectGroupInput[],
  groupBy: MyTasksGroupBy,
  touchedKeys: ReadonlySet<string> | undefined,
  retained: Map<string, ProjectGroupInput>,
  readLiveCompletedAt: (taskId: string) => string | null | undefined
): ProjectGroupInput[] => {
  if (!touchedKeys || touchedKeys.size === 0) return serverGroups;
  const presentKeys = new Set<string>();
  serverGroups.forEach((group) => {
    const key = serverGroupMergeKey(group, groupBy);
    presentKeys.add(key);
    if (touchedKeys.has(key)) retained.set(key, group);
  });
  const withLiveCompletion = (tasks: Task[]): Task[] =>
    tasks.flatMap((task) => {
      const completedAt = readLiveCompletedAt(task.id);
      return completedAt === undefined ? [] : [{ ...task, completedAt }];
    });
  const missing: ProjectGroupInput[] = [];
  retained.forEach((group, key) => {
    if (presentKeys.has(key) || !touchedKeys.has(key)) return;
    const tasks = withLiveCompletion(group.tasks);
    const completedTasks = withLiveCompletion(group.completedTasks);
    if (tasks.length + completedTasks.length === 0) return;
    missing.push({ ...group, tasks, completedTasks });
  });
  return missing.length > 0 ? [...serverGroups, ...missing] : serverGroups;
};

/**
 * True once every task in the group is completed (TA-10189) — zero open
 * tasks remaining, and at least one task exists at all (an empty group,
 * which shouldn't normally render, is not "complete"). Uses the server's
 * taskCount/completedCount totals when available (see MyTaskGroup doc
 * comment on why those beat tasks.length/completedTasks.length), matching
 * composeServerGroups' own optimistic-toggle adjustment so a card reacts on
 * the same render as its checkbox tap rather than waiting on a refetch.
 */
export const isGroupFullyComplete = (group: MyTaskGroup): boolean => {
  const openCount = group.taskCount ?? group.tasks.length;
  const completedCount = group.completedCount ?? group.completedTasks.length;
  return openCount === 0 && completedCount > 0;
};

/**
 * My Day / 7 Days (TA-10189 AC1): a fully-completed group card disappears
 * from the list outright — callers pass the result straight to the header's
 * `groups.length` count, so it decrements along with the card.
 */
export const excludeFullyCompleteGroups = (
  groups: MyTaskGroup[]
): MyTaskGroup[] => groups.filter((group) => !isGroupFullyComplete(group));

/**
 * All Tasks (TA-10189 AC2): a fully-completed group card sinks to the bottom
 * instead of disappearing. Stable partition — each partition keeps its own
 * relative order, only complete groups move as a block to the end.
 */
export const sortFullyCompleteGroupsLast = (
  groups: MyTaskGroup[]
): MyTaskGroup[] => {
  const open = groups.filter((group) => !isGroupFullyComplete(group));
  const complete = groups.filter((group) => isGroupFullyComplete(group));
  return [...open, ...complete];
};

/**
 * All Tasks (TA-10495 #10): the Unassigned card is highlighted and sits at the
 * front of the list, right after the separately rendered Unscheduled card.
 *
 * Deliberately a no-op for a fully-completed Unassigned group: callers apply
 * this AFTER sortFullyCompleteGroupsLast, so pinning unconditionally would
 * undo TA-10189 AC2 and float a card with zero open tasks above every group
 * that still has work left.
 */
export const pinUnassignedFirst = (groups: MyTaskGroup[]): MyTaskGroup[] => {
  const index = groups.findIndex((group) => group.key === UNASSIGNED_GROUP_KEY);
  if (index <= 0) return groups;
  const unassigned = groups[index];
  if (isGroupFullyComplete(unassigned)) return groups;
  return [unassigned, ...groups.slice(0, index), ...groups.slice(index + 1)];
};

/**
 * Whether a tab backed by a single server-sorted query may re-sort its rows
 * client-side (Jack's review of TA-10495 QA round 2, finding 1).
 *
 * Only OverdueToLow qualifies, because it is the one mode where the client and
 * the server implement the same rule. The other two do not:
 *
 * - LastUpdated maps to `TaskSortOption.UpdatedAtDesc`, so the server sorts on
 *   a real `updated_at`. `Task` exposes no `updatedAt`, so sortTasksInGroup
 *   substitutes `max(completedAt, createdAt)` — a task created in January and
 *   edited minutes ago would be pushed below one created yesterday and never
 *   touched.
 * - CreatedDate and LastUpdated both lack the overdue-first block that
 *   sortTasksInGroup applies in every mode, so re-sorting would invent one.
 *
 * My Day is exempt: it merges two due_group-scoped queries and cannot trust
 * either one's order alone, so it re-sorts in every mode by necessity.
 */
export const canSortGroupTasksOnClient = (orderBy: MyTasksOrderBy): boolean =>
  orderBy === MyTasksOrderBy.OverdueToLow;

/**
 * Applies the Order By rule to every group's open rows — for the default
 * OverdueToLow that is overdue first (soonest due date), then High → Medium →
 * Low with due date breaking ties (see sortTasksInGroup).
 *
 * Introduced for My Day (TA-10495 #7), which merges two separately-fetched
 * queries (Overdue + Current, see mergeServerGroups) and so concatenates two
 * independently-sorted lists rather than producing one coherently sorted list.
 * 7 Days and All Tasks each come from ONE server-sorted query, so they only
 * call this for OverdueToLow — see `canSortGroupTasksOnClient` above.
 *
 * Caveat, deliberate: each tab's group rows are paginated, so this sorts the
 * rows loaded so far. A later page can therefore introduce a row that sorts
 * above ones already on screen. That is the trade for an order that is correct
 * for what the user can actually see — the alternative was a globally
 * append-only order that could show High below Low after a priority edit.
 */
export const sortGroupTasks = (
  groups: MyTaskGroup[],
  orderBy: MyTasksOrderBy = MyTasksOrderBy.OverdueToLow
): MyTaskGroup[] =>
  groups.map((group) => ({
    ...group,
    tasks: sortTasksInGroup(group.tasks, undefined, orderBy),
    // Only OverdueToLow has a server rule the client can reproduce for
    // completed rows; the others keep the server's order.
    completedTasks:
      orderBy === MyTasksOrderBy.OverdueToLow
        ? sortCompletedTasksInGroup(group.completedTasks)
        : group.completedTasks,
  }));

export const isGroupEmpty = (group: MyTaskGroup): boolean =>
  group.tasks.length === 0 &&
  !(group.taskCount ?? 0) &&
  group.completedTasks.length === 0 &&
  !(group.completedCount ?? 0);

export const excludeEmptyGroups = (groups: MyTaskGroup[]): MyTaskGroup[] =>
  groups.filter((group) => !isGroupEmpty(group));

/**
 * 7 Days shows no completed items (AC3); its query excludes them, but a task
 * completed on the tab is re-bucketed into `completedTasks` by its optimistic
 * completedAt before the refetch drops it. Clearing the bucket makes the row
 * leave at once instead of briefly showing under "Show completed" (TA-11016).
 */
export const withoutCompletedTasks = (groups: MyTaskGroup[]): MyTaskGroup[] =>
  groups.map((group) =>
    group.completedTasks.length > 0 ? { ...group, completedTasks: [] } : group
  );

/**
 * Keeps already-shown items where they were and slots new ones in by their
 * computed position (TA-11016): items present in `previousKeys` keep that
 * relative order; an item not seen before goes right after whatever precedes
 * it in the freshly computed order (or first, if nothing does).
 *
 * Used at both levels of My Tasks. Group cards: they are ranked by recent
 * task activity and completing a task is activity, so without this the
 * project card jumped to the top; the design keeps it where it is. Rows,
 * while a toggle is in flight: the toggled task lands in its new bucket at
 * the place it will hold once the refetch confirms it, instead of being
 * parked at the end and jumping there later (TA-10189, TA-11016).
 */
export const applyStickyOrder = <T>(
  items: T[],
  previousKeys: string[],
  keyOf: (item: T) => string
): T[] => {
  const previousIndex = new Map(previousKeys.map((key, i) => [key, i]));
  const result = items
    .filter((item) => previousIndex.has(keyOf(item)))
    .sort(
      (a, b) =>
        (previousIndex.get(keyOf(a)) ?? 0) - (previousIndex.get(keyOf(b)) ?? 0)
    );
  let anchorKey: string | null = null;
  items.forEach((item) => {
    const key = keyOf(item);
    if (!previousIndex.has(key)) {
      const at =
        anchorKey === null
          ? 0
          : result.findIndex((placed) => keyOf(placed) === anchorKey) + 1;
      result.splice(at, 0, item);
    }
    anchorKey = key;
  });
  return result;
};

/**
 * Puts the `pinnedKeys` items back at the index they had in `previousKeys`,
 * and lets every other item follow the freshly computed order (TA-11016).
 *
 * Used for PROJECT GROUP cards once no toggle is in flight: only the cards
 * this session's own taps touched stay put (completing a task is activity,
 * which would otherwise lift its card to the top — the design keeps it where
 * it is), while any other reorder, e.g. a teammate's activity on another
 * project landing with a poll, still applies. Pinned items are re-inserted in
 * ascending previous index so they don't displace each other; one missing
 * from `previousKeys` is treated as unpinned.
 */
export const applyPinnedPositions = <T>(
  items: T[],
  previousKeys: string[],
  pinnedKeys: ReadonlySet<string>,
  keyOf: (item: T) => string
): T[] => {
  const previousIndex = new Map(previousKeys.map((key, i) => [key, i]));
  const isPinned = (item: T) =>
    pinnedKeys.has(keyOf(item)) && previousIndex.has(keyOf(item));
  const result = items.filter((item) => !isPinned(item));
  items
    .filter(isPinned)
    .sort(
      (a, b) =>
        (previousIndex.get(keyOf(a)) ?? 0) - (previousIndex.get(keyOf(b)) ?? 0)
    )
    .forEach((item) => {
      const at = Math.min(previousIndex.get(keyOf(item)) ?? 0, result.length);
      result.splice(at, 0, item);
    });
  return result;
};

export type DueFilterRange = {
  begin: dayjs.Dayjs;
  end: dayjs.Dayjs;
};

export type MyTasksDateWindows = {
  filterBegin?: string;
  filterEnd?: string;
  overdueBegin?: string;
  overdueEnd?: string;
  todayEnd?: string;
  weekBegin?: string;
  weekEnd?: string;
  weekRangeIsEmpty: boolean;
};

/**
 * The due-date windows each My Tasks tab queries with, derived from the
 * filter sheet's selected range. Pure (`now` is injected) so every clamp is
 * unit-testable — see TasksListView for how each field is consumed.
 *
 * - Overdue only narrows when the selected range actually reaches into the
 *   past; a future-only chip (Tomorrow, Next 3 days) has no overlap with the
 *   server's implicit due<today truncation and would silently empty the Hero
 *   carousel.
 * - 7 Days is the INTERSECTION of the nominal next-7-days window with the
 *   filter (TA-10495 #12). Clamping only the start let a custom 1–30 Sept
 *   range render a full month under a tab labelled "7 Days"; clamping only
 *   the end produced an inverted range that always returned zero groups.
 *   `weekRangeIsEmpty` lets the caller skip the query entirely rather than
 *   ask the server for a backwards range.
 */
export const computeMyTasksDateWindows = (
  dueFilterRange: DueFilterRange | null | undefined,
  now: dayjs.Dayjs = dayjs()
): MyTasksDateWindows => {
  const iso = (d: dayjs.Dayjs | undefined): string | undefined =>
    d ? d.format('YYYY-MM-DDTHH:mm:ssZ') : undefined;
  const laterOf = (a: dayjs.Dayjs, b?: dayjs.Dayjs) => (b?.isAfter(a) ? b : a);
  const earlierOf = (a: dayjs.Dayjs, b?: dayjs.Dayjs) =>
    b?.isBefore(a) ? b : a;

  const startOfToday = now.startOf('day');
  const endOfToday = now.endOf('day');
  const tomorrowStart = now.add(1, 'day').startOf('day');
  const defaultWeekEnd = now.add(7, 'day').endOf('day');

  const overdueFilterApplies =
    !!dueFilterRange && dueFilterRange.begin.isBefore(startOfToday);

  const weekBeginDate = laterOf(tomorrowStart, dueFilterRange?.begin);
  const weekEndDate = earlierOf(defaultWeekEnd, dueFilterRange?.end);

  return {
    filterBegin: iso(dueFilterRange?.begin),
    filterEnd: iso(dueFilterRange?.end),
    overdueBegin: overdueFilterApplies ? iso(dueFilterRange?.begin) : undefined,
    overdueEnd: overdueFilterApplies ? iso(dueFilterRange?.end) : undefined,
    todayEnd: iso(earlierOf(endOfToday, dueFilterRange?.end)),
    weekBegin: iso(weekBeginDate),
    weekEnd: iso(weekEndDate),
    weekRangeIsEmpty: weekEndDate.isBefore(weekBeginDate),
  };
};
